/**
 * 모던 통계 카드 컴포넌트
 * 20년차 디자이너 관점에서 개선된 통계 카드
 */

import React from 'react';
import styled, { css } from 'styled-components';
import {
    Users,
    CreditCard,
    Target,
    TrendingUp,
    TrendingDown,
    Minus
} from 'lucide-react';
import { colors, typography, spacing, borderRadius, shadows, animations } from '../../styles/designTokens';

// ===== 타입 정의 =====
export interface ModernStatsCardProps {
    title: string;
    value: number;
    change?: {
        text: string;
        color: string;
        icon: string;
    };
    // 카드 본문 하단에 추가로 표기할 메타 정보 (예: 자동/수동/활성 키워드 수)
    metaItems?: Array<{
        label: string;
        value: string | number;
        color?: string;
    }>;
    formatNumber: (num: number) => string;
    clickable?: boolean;
    isActive?: boolean;
    onClick?: () => void;
    className?: string;
}

// ===== 아이콘 매핑 =====
const getCardIcon = (title: string) => {
    if (title.includes('결제 금액')) {
        return CreditCard;
    }
    switch (title) {
        case '현재 고객':
            return Users;
        case '전일 결제 금액':
            return CreditCard;
        case '활성 키워드':
            return Target;
        default:
            return Target;
    }
};

const getChangeIcon = (icon: string) => {
    switch (icon) {
        case '↗':
            return TrendingUp;
        case '↘':
            return TrendingDown;
        case '●':
        default:
            return Minus;
    }
};

// ===== 스타일드 컴포넌트 =====
const CardContainer = styled.div<{
    $clickable: boolean;
    $isActive: boolean;
}>`
  background: ${colors.semantic.background.primary};
  border: 1px solid ${({ $isActive, $clickable }) => {
        if ($isActive) return colors.primary[300];
        if ($clickable) return colors.semantic.border.secondary;
        return colors.semantic.border.primary;
    }};
  border-radius: ${borderRadius.xl};
  padding: ${spacing[6]};
  box-shadow: ${({ $isActive, $clickable }) => {
        if ($isActive) return shadows.primary;
        if ($clickable) return shadows.md;
        return shadows.sm;
    }};
  cursor: ${({ $clickable }) => $clickable ? 'pointer' : 'default'};
  transition: all ${animations.duration.normal} ${animations.easing.smooth};
  transform: ${({ $isActive }) => $isActive ? 'translateY(-4px)' : 'none'};
  position: relative;
  overflow: hidden;
  opacity: ${({ $clickable }) => $clickable ? 1 : 0.85};
  
  /* 클릭 가능한 카드 표시 */
  ${({ $clickable }) => $clickable && css`
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 3px;
      background: linear-gradient(90deg, ${colors.primary[500]} 0%, ${colors.primary[600]} 100%);
      opacity: 0.6;
    }
  `}
  
  /* 활성 상태 그라데이션 배경 */
  ${({ $isActive }) => $isActive && css`
    background: linear-gradient(135deg, ${colors.primary[50]} 0%, ${colors.semantic.background.primary} 100%);
    border-color: ${colors.primary[400]};
    opacity: 1;
    
    &::before {
      opacity: 1;
    }
  `}
  
  /* 호버 효과 - 클릭 가능한 카드만 */
  &:hover {
    ${({ $clickable }) => $clickable && css`
      transform: translateY(-4px);
      box-shadow: ${shadows.lg};
      border-color: ${colors.primary[400]};
      background: linear-gradient(135deg, ${colors.primary[50]} 0%, ${colors.semantic.background.primary} 100%);
      opacity: 1;
      
      &::before {
        opacity: 1;
      }
    `}
  }
  
  /* 모바일 최적화 */
  @media (max-width: 640px) {
    padding: ${spacing[5]};
    border-radius: ${borderRadius.lg};
  }
  
  @media (max-width: 480px) {
    padding: ${spacing[4]};
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${spacing[4]};
`;

const CardTitle = styled.h3`
  font-size: ${typography.fontSize.sm};
  color: ${colors.semantic.text.tertiary};
  margin: 0;
  font-weight: ${typography.fontWeight.medium};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  @media (max-width: 640px) {
    font-size: ${typography.fontSize.xs};
  }
`;

const CardIcon = styled.div<{ $isActive: boolean; $clickable: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: ${borderRadius.lg};
  background: ${({ $isActive, $clickable }) => {
        if ($isActive) {
            return `linear-gradient(135deg, ${colors.primary[500]} 0%, ${colors.primary[600]} 100%)`;
        }
        if ($clickable) {
            return colors.primary[100];
        }
        return colors.semantic.background.tertiary;
    }};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $isActive, $clickable }) => {
        if ($isActive) return colors.semantic.text.inverse;
        if ($clickable) return colors.primary[600];
        return colors.semantic.text.tertiary;
    }};
  transition: all ${animations.duration.fast} ${animations.easing.smooth};
  border: ${({ $clickable }) =>
        $clickable ? `1px solid ${colors.primary[200]}` : 'none'
    };
  
  svg {
    width: 20px;
    height: 20px;
  }
  
  @media (max-width: 640px) {
    width: 36px;
    height: 36px;
    
    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

const CardValue = styled.div<{ $hasMeta?: boolean }>`
  font-size: ${typography.fontSize['3xl']};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.semantic.text.primary};
  margin-bottom: ${({ $hasMeta }) => ($hasMeta ? spacing[2] : spacing[3])};
  line-height: 1.2;
  
  @media (max-width: 640px) {
    font-size: ${typography.fontSize['2xl']};
    margin-bottom: ${spacing[2]};
  }
  
  @media (max-width: 480px) {
    font-size: ${typography.fontSize.xl};
  }
`;

const CardChange = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  color: ${({ $color }) => $color};
  
  @media (max-width: 640px) {
    font-size: ${typography.fontSize.xs};
  }
  
  .change-icon {
    margin-right: ${spacing[2]};
    display: flex;
    align-items: center;
    
    svg {
      width: 16px;
      height: 16px;
      
      @media (max-width: 640px) {
        width: 14px;
        height: 14px;
      }
    }
  }
`;

const CardMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 10px;
  margin-bottom: ${spacing[2]};
`;

const MetaChip = styled.div<{ $color?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid ${colors.semantic.border.primary};
  background: ${colors.secondary[50]};
  color: ${({ $color }) => $color || colors.secondary[600]};
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;

  .meta-label {
    font-weight: 800;
  }
  .meta-value {
    color: ${({ $color }) => $color || colors.secondary[800]};
  }
`;

// ===== 메인 컴포넌트 =====
export const ModernStatsCard: React.FC<ModernStatsCardProps> = ({
    title,
    value,
    change,
    metaItems,
    formatNumber,
    clickable = false,
    isActive = false,
    onClick,
    className
}) => {
    const IconComponent = getCardIcon(title);
    const ChangeIconComponent = change ? getChangeIcon(change.icon) : Minus;

    return (
        <CardContainer
            $clickable={clickable}
            $isActive={isActive}
            onClick={clickable ? onClick : undefined}
            onKeyDown={
                clickable && onClick
                    ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              onClick();
                          }
                      }
                    : undefined
            }
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            aria-pressed={clickable ? isActive : undefined}
            aria-label={
                clickable ? `${title}, 아래 표 영역 표시를 전환합니다` : undefined
            }
            className={className}
        >
            <CardHeader>
                <CardTitle>{title}</CardTitle>
                <CardIcon $isActive={isActive} $clickable={clickable}>
                    <IconComponent />
                </CardIcon>
            </CardHeader>

            <CardValue $hasMeta={!!metaItems && metaItems.length > 0}>{formatNumber(value)}</CardValue>

            {metaItems && metaItems.length > 0 && (
              <CardMeta>
                {metaItems.map(item => (
                  <MetaChip
                    key={item.label}
                    $color={item.color}
                    title={`${item.label}: ${item.value}`}
                  >
                    <span className="meta-label">{item.label}</span>
                    <span className="meta-value">
                      {typeof item.value === 'number' ? formatNumber(item.value) : item.value}
                    </span>
                  </MetaChip>
                ))}
              </CardMeta>
            )}

            {change && (
                <CardChange $color={change.color}>
                    <div className="change-icon">
                        <ChangeIconComponent />
                    </div>
                    {change.text}
                </CardChange>
            )}
        </CardContainer>
    );
};

export default ModernStatsCard;
